// A AGENDA da Gestao de Vendas — a grade da semana ([DA-672]).
//
// Pedido dele em 04/09/2026, com o print do Google Agenda: *"criar mais uma aba Agenda, cada
// vendedora visualiza so a sua. Nela seria exibido todos os Andamentos com a hora e o comentario"*.
//
// ⚠️ **O que e' reaproveitado, e o que e' novo.** A regua de ESTADO vem inteira do `shared`
// (`agenda-estado.ts`, [DA-575]) — a mesma que pinta a Agenda do AppCel, com as cores que ele
// ditou em 19/08 e o SELO escrito por cima da cor ([[user_daltonismo]]: ele nao distingue verde de
// vermelho, e a paleta tem vermelho, laranja e amarelo em sequencia). O que e' novo aqui e' so o
// DESENHO: a do celular empilha os 7 dias um embaixo do outro, e a referencia dele poe os dias
// LADO A LADO, em colunas.
//
// ⚠️ **Desde 04/09/2026 o FUNDO do cartao nao e' mais do estado, e sim do ROTULO** que ele pintou
// em `situacoes_andamento` ([DA-674]) — pedido dele com o print da grade toda azul. O estado saiu
// pro selo; ver `CartaoAndamento`, onde os dois eixos estao explicados.
//
// ⚠️ **Grade em CSS Grid, com os cartoes EMPILHADOS dentro da celula da hora** — nao posicionamento
// absoluto por minuto. Andamento e' um compromisso sem duracao (marcar "ligar 14h", nao "das 14h
// as 15h"), entao nao ha altura a calcular; e dois contatos na mesma hora se leem melhor
// empilhados do que sobrepostos. O volume tambem manda: sao ~9 pedidos contataveis por semana.
//
// ⚠️ **As horas VAZIAS ficam.** Foi a licao da [DA-644], comprada com o Ruan me corrigindo: eu
// quis recolher as faixas vazias da grade do AppCel e ele respondeu *"E para marcar onde cabe"*.
// Numa tela de DECIDIR, o buraco na quinta de manha e' a resposta pra "onde encaixo esse cliente?".
// No monitor isso nem custa rolagem — as 13 faixas cabem em ~520px (no celular davam 6 telas).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle, Pencil, RefreshCw } from 'lucide-react';
import {
  chaveDoDia, horaDoDia, faixaDaGrade,
  estadoDoCompromisso, pinturaDoEstado, seloDoCompromisso,
  formatMoeda, instanteNoFuso, type StatusCompromisso,
} from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { pinturaSeEscolhida } from './CoresDoRotulo';

export interface LinhaAgenda {
  id: number;
  orcamento_id: number;
  quando: string;
  comentario: string | null;
  criado_em: string;
  situacao: string;
  numero: string;
  total: string;
  /** O ID da situacao, e nao so' o nome: e' com ele que a caixa de edicao reabre com o rotulo certo. */
  situacao_id: number;
  whatsapp_enviado: string | null;
  contato_id: number | null;
  vendedora: string | null;
  cliente: string | null;
  estado_base: StatusCompromisso;
  situacao_cor_fundo: string | null;
  situacao_cor_fonte: string | null;
}

const DIAS_SEMANA = ['DOM.', 'SEG.', 'TER.', 'QUA.', 'QUI.', 'SEX.', 'SÁB.'];

/**
 * Aritmetica de dia em cima do TEXTO `YYYY-MM-DD`, com um Date ancorado ao MEIO-DIA UTC.
 *
 * ⚠️ Meio-dia, e nao meia-noite: a loja esta em UTC-3, entao `T00:00:00Z` cai as 21h do dia
 * ANTERIOR no relogio dela e a semana inteira anda um dia pra tras. Ao meio-dia a folga de 12h
 * cobre qualquer deslocamento e a data de calendario nunca vira.
 */
function diaMais(chave: string, dias: number): string {
  const d = new Date(`${chave}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** O domingo da semana daquela data — a referencia dele abre a semana no domingo. */
function domingoDa(chave: string): string {
  const d = new Date(`${chave}T12:00:00Z`);
  return diaMais(chave, -d.getUTCDay());
}

const MES_CURTO = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', month: 'short', year: 'numeric' });

export function AgendaVendas({ onAbrirOrcamento, onWhatsApp, onMarcar, onEditar, sinalRecarga }: {
  onAbrirOrcamento: (orcamentoId: number) => void;
  onWhatsApp: (l: LinhaAgenda) => void;
  /**
   * Duplo-clique numa hora LIVRE e FUTURA — pedido dele em 08/09/2026: *"seria bom poder clicar
   * duas vezes no campo da agenda para poder marcar novos compromissos"*.
   *
   * ⚠️ Inverte o ponto de partida da tela. Ate aqui so' dava pra marcar A PARTIR DO PEDIDO (achar a
   * linha na aba, clicar em Andamento, digitar dia e hora) e a Agenda era so' leitura. O gesto
   * novo marca A PARTIR DO BURACO, que e' exatamente pra que as horas vazias ficam na grade
   * ([DA-644]: *"e para marcar onde cabe"*) — a tela mostrava onde cabia e nao deixava agir dali.
   */
  onMarcar?: (data: string, hora: string) => void;
  /** Editar/excluir um compromisso ja marcado — pelo lapis do cartao ([DA-684]). */
  onEditar?: (l: LinhaAgenda) => void;
  /**
   * Contador que a tela de fora incrementa pra pedir uma releitura da semana.
   *
   * ⚠️ Um NUMERO, e nao um `key`: trocar a `key` remontaria o componente e jogaria fora a semana
   * que a pessoa navegou — quem marcou um compromisso na semana que vem seria devolvido pra semana
   * de hoje no instante em que gravou.
   */
  sinalRecarga?: number;
}) {
  const toast = useToast();
  const hoje = chaveDoDia(new Date());
  const [inicio, setInicio] = useState(() => domingoDa(hoje));
  const [linhas, setLinhas] = useState<LinhaAgenda[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [soMeu, setSoMeu] = useState(false);
  // Um relogio proprio, que anda: sem ele o cartao das 14h continuaria "falta 1h" as 15h, e a cor
  // so mudaria quando alguem recarregasse a pagina. De minuto em minuto basta — a fronteira mais
  // fina da regua e' o "falta menos de uma hora".
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<{ linhas: LinhaAgenda[]; so_meu: boolean }>(
        '/api/fila-retomada/agenda', { query: { inicio, dias: 7 } },
      );
      setLinhas(r.linhas);
      setSoMeu(r.so_meu);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao carregar a agenda');
    } finally {
      setCarregando(false);
    }
    // ⛔ **`sinalRecarga` entra nas dependencias, e nao e' enfeite.** A Agenda carrega SOZINHA (por
    // semana, nao por balde), entao o `carregar()` da tela de fora nunca a alcancava: gravar um
    // compromisso mostrava o toast de sucesso e deixava o cartao velho na grade. Ele leu isso como
    // "nao esta salvando" e regravou o mesmo texto duas vezes — as duas gravaram, medido no log e
    // no banco (08/09/2026). Tela que se contradiz com o proprio aviso de sucesso e' pior que tela
    // que falha: quem opera repete o gesto ate confiar.
  }, [inicio, sinalRecarga, toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => diaMais(inicio, i)), [inicio]);

  // A faixa de horas ESTICA pra caber o que existe (`faixaDaGrade`): compromisso marcado pras 20h
  // nao pode sumir da tela porque cai fora do horario comercial — agenda que esconde compromisso
  // e' pior que agenda nenhuma, porque quem opera confia nela.
  const porCelula = useMemo(() => {
    const mapa = new Map<string, LinhaAgenda[]>();
    for (const l of linhas) {
      const d = new Date(l.quando);
      const chave = `${chaveDoDia(d)}|${Number(horaDoDia(d).slice(0, 2))}`;
      const atual = mapa.get(chave);
      if (atual) atual.push(l); else mapa.set(chave, [l]);
    }
    return mapa;
  }, [linhas]);

  const [horaInicio, horaFim] = useMemo(
    () => faixaDaGrade(linhas.map((l) => Number(horaDoDia(new Date(l.quando)).slice(0, 2)))),
    [linhas],
  );
  const horas = useMemo(
    () => Array.from({ length: horaFim - horaInicio + 1 }, (_, i) => horaInicio + i),
    [horaInicio, horaFim],
  );

  const rotuloPeriodo = (() => {
    const a = MES_CURTO.format(new Date(`${inicio}T12:00:00Z`));
    const b = MES_CURTO.format(new Date(`${dias[6]}T12:00:00Z`));
    return a === b ? a : `${a} – ${b}`;
  })();

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden" style={{ backgroundColor: 'var(--panel-bg)' }}>
      {/* Cabecalho de navegacao — molde da referencia dele: Hoje, setas, periodo. */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 flex-wrap">
        <button type="button" className="btn-secundario text-xs px-3 py-1" onClick={() => setInicio(domingoDa(hoje))}>
          Hoje
        </button>
        <button type="button" aria-label="Semana anterior" title="Semana anterior"
          className="btn-secundario text-xs px-2 py-1" onClick={() => setInicio((i) => diaMais(i, -7))}>
          <ChevronLeft size={14} />
        </button>
        <button type="button" aria-label="Proxima semana" title="Proxima semana"
          className="btn-secundario text-xs px-2 py-1" onClick={() => setInicio((i) => diaMais(i, 7))}>
          <ChevronRight size={14} />
        </button>
        <span className="text-sm font-semibold ml-1">{rotuloPeriodo}</span>
        <span className="text-xs text-gray-500 ml-auto">
          {linhas.length} {linhas.length === 1 ? 'andamento' : 'andamentos'}{soMeu ? ' · so os seus' : ''}
        </span>
        <button type="button" className="btn-secundario text-xs px-2 py-1 flex items-center gap-1" onClick={() => void carregar()}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {carregando ? (
        <div className="text-center text-gray-400 py-12 text-sm">Carregando...</div>
      ) : (
        // A grade rola dentro da propria moldura — 7 colunas nao cabem em tela estreita, e cortar
        // calado esconderia o sabado sem nada avisando que ele existe.
        <div className="overflow-x-auto">
          <div style={{ minWidth: 900 }}>
            {/* Cabecalho dos dias */}
            <div className="grid sticky top-0 z-10" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))', backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
              <div />
              {dias.map((d) => {
                const ehHoje = d === hoje;
                const numero = d.slice(8, 10);
                return (
                  <div key={d} className="text-center py-1.5 border-l border-gray-200">
                    <div className="text-[10px] tracking-wide opacity-80">{DIAS_SEMANA[new Date(`${d}T12:00:00Z`).getUTCDay()]}</div>
                    {/* HOJE marcado como na referencia: numero dentro de um circulo cheio. E' a
                        unica pintura da grade que nao vem da regua de estado — ela fala do
                        CALENDARIO, nao do compromisso. */}
                    <div
                      className="inline-flex items-center justify-center text-sm font-semibold"
                      style={ehHoje
                        ? { backgroundColor: '#2563eb', color: '#ffffff', borderRadius: '9999px', width: 26, height: 26 }
                        : undefined}
                    >
                      {numero}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* As faixas de hora */}
            {horas.map((h) => (
              <div key={h} className="grid" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))', minHeight: 44 }}>
                <div className="text-[11px] text-gray-400 text-right pr-2 pt-1 border-t border-gray-100">
                  {String(h).padStart(2, '0')}:00
                </div>
                {dias.map((d) => {
                  const daCelula = porCelula.get(`${d}|${h}`) ?? [];
                  // ⛔ **Hora que ja passou nao abre nada.** O servidor recusa compromisso no
                  // passado (`recusaDesfecho`), e tela que oferece o que o servidor vai negar e'
                  // beco sem saida — a pessoa preencheria tudo pra levar um erro no fim.
                  // O instante sai do `instanteNoFuso` do `shared`, o mesmo que o servidor usa pra
                  // montar o compromisso: `new Date('...T09:00')` leria no fuso de QUEM RODA.
                  const [ano, mes, dia] = d.split('-').map(Number) as [number, number, number];
                  const podeMarcar = !!onMarcar
                    && instanteNoFuso(ano, mes, dia, h, 0).getTime() > agora.getTime();
                  return (
                    <div
                      key={d}
                      className="border-t border-l border-gray-100 p-0.5 flex flex-col gap-0.5"
                      style={podeMarcar ? { cursor: 'cell' } : undefined}
                      title={podeMarcar ? 'Clique duas vezes pra marcar um compromisso nesta hora' : undefined}
                      // ⚠️ `e.target === e.currentTarget` — o duplo-clique so' conta quando cai no
                      // VAZIO da celula. Sem isso ele borbulha do cartao: dois cliques num
                      // compromisso ja marcado abririam a caixa de marcar outro por cima dele.
                      // Assim ainda da' pra acrescentar um segundo compromisso na mesma hora,
                      // clicando na parte livre abaixo do cartao.
                      onDoubleClick={podeMarcar
                        ? (e) => { if (e.target === e.currentTarget) onMarcar!(d, `${String(h).padStart(2, '0')}:00`); }
                        : undefined}
                    >
                      {daCelula.map((l) => (
                        <CartaoAndamento key={l.id} linha={l} agora={agora}
                          onAbrir={() => onAbrirOrcamento(l.orcamento_id)}
                          onWhatsApp={() => onWhatsApp(l)}
                          onEditar={() => onEditar?.(l)} />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {!carregando && linhas.length === 0 && (
        <div className="text-center py-8 px-6 border-t border-gray-200">
          <div className="font-semibold mb-1">Semana sem andamento marcado</div>
          <div className="text-sm text-gray-500 max-w-lg mx-auto">
            Os compromissos aparecem aqui quando alguem usa o botao <strong>Andamento</strong> na fila —
            a hora escolhida vira a faixa da grade. As horas vazias sao de proposito: e' nelas que da'
            pra ver onde cabe um contato novo. <strong>Clique duas vezes numa hora livre</strong> pra
            marcar dali mesmo.
          </div>
        </div>
      )}
    </div>
  );
}

function CartaoAndamento({ linha, agora, onAbrir, onWhatsApp, onEditar }: {
  linha: LinhaAgenda; agora: Date; onAbrir: () => void; onWhatsApp: () => void; onEditar: () => void;
}) {
  const quando = new Date(linha.quando);
  const estado = estadoDoCompromisso(quando, linha.estado_base, agora);
  const pintura = pinturaDoEstado(estado);
  const selo = seloDoCompromisso(quando, linha.estado_base, agora);

  // OS DOIS EIXOS, cada um no seu lugar (pedido dele em 04/09/2026, com o print da grade toda azul).
  //
  // A [DA-672] p.2 estabeleceu que sao dois: o ROTULO diz *por que o pedido esta parado*
  // (REAGENDADO / NEGOCIACAO / AGUARDANDO) e o ESTADO diz *o contato aconteceu?*. Ate aqui o cartao
  // pintava so o segundo — e o azul de 'concluido' (#1d4ed8) e' quase o mesmo `#1e40af` que ele
  // gravou no REAGENDADO, entao a grade parecia estar usando a paleta dele e nao estava.
  //
  // ⚠️ **O fundo passa a ser o do rotulo; o estado vira SELO, nunca some.** Ele escolheu isso com o
  // mockup na frente, sabendo que o vermelho de "venceu" deixa de pintar o cartao inteiro.
  const daCor = pinturaSeEscolhida(linha.situacao_cor_fundo, linha.situacao_cor_fonte);
  const rotuloPintado = !!(linha.situacao_cor_fundo || linha.situacao_cor_fonte);
  // Sem numero nao ha a quem chamar. ⚠️ A guarda `da_casa` que existia aqui SAIU em 08/09/2026:
  // o pedido mandado pro celular da propria vendedora nao chega mais nesta grade — a rota o exclui
  // na origem, junto com as abas e os contadores. Guarda que so pode dar um valor nao guarda nada,
  // e ainda faria parecer que a Agenda decide algo que ela nao decide mais.
  const temWhats = !!linha.whatsapp_enviado;

  return (
    <div
      className="rounded px-1.5 py-1 text-[11px] leading-tight"
      style={
        // ⚠️ Rotulo SEM cor gravada cai na pintura do estado — o comportamento de antes desta onda.
        // Situacao nova nasce sem cor, e um cartao branco e mudo seria pior que o que ja havia.
        rotuloPintado
          ? { ...daCor, border: '1px solid rgba(0,0,0,0.18)' }
          : {
              backgroundColor: pintura.fundo ?? 'transparent',
              color: pintura.texto,
              border: pintura.borda ? `1px solid ${pintura.borda}` : '1px solid transparent',
            }
      }
    >
      <div className="flex items-center gap-1">
        <span className="font-bold tabular-nums">{horaDoDia(quando)}</span>
        {selo && (
          // O CHIP BRANCO com a letra na cor do estado. E' pra isso que o campo `contorno` existe no
          // `shared`: a mesma cor, na versao que se le sobre claro. Com o fundo agora vindo do rotulo,
          // um selo pintado com a cor cheia sumiria — o azul de "feito" sobre o azul do REAGENDADO foi
          // exatamente o que o mockup pegou, antes de existir codigo.
          <span
            className="rounded px-1 text-[9px] font-bold"
            style={{ backgroundColor: '#ffffff', color: pintura.contorno, border: `1px solid ${pintura.contorno}` }}
          >
            {selo}
          </span>
        )}
        {/* Os dois icones andam juntos na ponta direita. O `ml-auto` saiu do WhatsApp e veio pra
            ca: com dois botoes, ele no primeiro empurraria o segundo pra fora do alinhamento. */}
        <span className="ml-auto flex items-center gap-1.5">
          {/* ⚠️ O lapis e' VISIVEL, nao um duplo-clique escondido. Editar compromisso e' o tipo de
              coisa que so' se procura depois de marcar errado — e quem esta consertando pressa nao
              descobre gesto secreto ([DA-667]). O duplo-clique na celula VAZIA segue sendo o
              "marcar novo"; aqui o alvo e' o cartao, e o gesto e' um clique so'. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEditar(); }}
            title="Editar ou excluir este compromisso"
            aria-label="Editar compromisso"
            style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', lineHeight: 0 }}
          >
            <Pencil size={12} />
          </button>
          {temWhats && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onWhatsApp(); }}
              title={`Abrir a conversa de ${linha.cliente ?? linha.numero} no Atendimento`}
              aria-label="Abrir conversa no Atendimento"
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', lineHeight: 0 }}
            >
              <MessageCircle size={13} />
            </button>
          )}
        </span>
      </div>
      <button
        type="button"
        onClick={onAbrir}
        title={`Abrir ${linha.numero}`}
        className="text-left w-full"
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit' }}
      >
        <div className="truncate font-semibold">{linha.cliente ?? linha.numero}</div>
        <div className="truncate opacity-80">{linha.situacao} · {formatMoeda(Number(linha.total))}</div>
        {/* O comentario e' o que ele pediu pra ver na agenda — e' ele que carrega o caso. Duas
            linhas no maximo; o resto fica no `title`, sem esticar a faixa da hora. */}
        {linha.comentario && (
          <div className="opacity-75" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={linha.comentario}>
            {linha.comentario}
          </div>
        )}
      </button>
    </div>
  );
}
